import React, { useEffect, useState } from "react";

import { Header } from "components/Header";
import { SearchBar } from "components/SearchBar";
import { DataTable } from "components/DataTable";
import { AlertModal } from "components/Modals/AlertModal";
import { MoreClientDetailsModal } from "components/Modals/MoreClientDetailsModal";
import { StepRegisterClient } from "components/Modals/StepRegister/StepRegisterClient";

import { IClient } from "interfaces/Client";

import {
  handleClientActiveSubmit,
  handleClientDeleteSubmit,
} from "services/handles/ClientHandles";

import { DashboardTemplate } from "templates/DashboardTemplate";

import { useClientRegister } from "contexts/register/ClientContext";
import { CheckAlert } from "utils/checkAlert";
import debouncer from "utils/debouncer";

import { IFilterWhereOptions, useDataList } from "hooks/useDataList";
import { MoreDetailsModal } from "components/Modals/MoreDetailsModal";
import { useAuth } from "contexts/authContext";

export function Clients() {
  const [client, setClient] = useState<IClient>();
  const [page, setPage] = useState(1);
  const { storeClient, hasChanges, setHasChanges } = useClientRegister();

  const [filterWhere, setFilterWhere] = useState<IFilterWhereOptions<IClient>>()

  const [registerModal, setRegisterModal] = useState(false);
  const [customerClient, setCustomerClient] = useState(false);
  const [productDetails, setProductDetails] = useState(false);
  const [activationModal, setActivationModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { role } = useAuth()

  const { data, isFetching } = useDataList<IClient>({
    queryKey: 'listAllClients',
    route: 'clients',
    filter: {
      size: 10,
      page,
      params: filterWhere,
      relations: { product: true },
      order: {
        name:"ASC"
      }
    }
  })

  useEffect(() => {
    CheckAlert();
  }, []);

  function handleMenuClick(menuItemId: string, data: IClient) {
    if (!data) return;

    setClient(data);

    switch (menuItemId) {
      case "customerClient":
        storeClient(data);
        setCustomerClient(!customerClient);
        break;
      case "edit":
        storeClient(data);
        setRegisterModal(!registerModal);
        break;
      case "activate":
        setActivationModal(!activationModal);
        break;
      case "deactivate":
        setDeleteModal(!deleteModal);
        break;
      case "product":
        storeClient(data);
        setProductDetails(!productDetails);
        break;
    }
  }

  function handleSearchChange(queryString: string) {
    if (queryString) {
      return setFilterWhere([
        { name: `%${queryString}%` },
        { tradingName: `%${queryString}%` },
        { managerName: `%${queryString}%` },
        { cnpj: `%${queryString}%` },
      ])
    }
  }

  const debounceClientSearch = debouncer(handleSearchChange, 800);

  return (
    <DashboardTemplate
      hasTableWrapper
      total={data?.total || 0}
      page={page}
      setPage={setPage}
    >
      <Header title="Clientes" />
      <SearchBar
        type="text"
        name="filter"
        id="filter"
        placeholder="Procure por nome, CNPJ, produto..."
        buttonText="Cadastrar cliente"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) {
            debounceClientSearch(event.target.value)
          } else {
            setFilterWhere(undefined);
          }
        }}
        required={false}
        onClick={() => setRegisterModal(true)}
      />

      <DataTable
        data={data?.items}
        isFetching={isFetching}
        headers={[
          { field: "name", label: "Nome do cliente" },
          { field: "active", label: "Registro", type: "status" },
          { field: "cnpj", label: "CNPJ" },
          { field: "tradingName", label: "Nome fantasia" },
          { field: "managerName", label: "Razão social" },
          {
            field: "product",
            label: "Produto",
            renderColumn: (client) => <span>{client.product?.name}</span>,
          },
        ]}
        menuActions={[
          {
            id: "customerClient",
            label: "Dados do cliente",
            onClick: handleMenuClick,
          },
          { id: "edit", label: "Editar cliente", onClick: handleMenuClick },
          {
            id: "product",
            label: "Dados do produto",
            onClick: handleMenuClick,
          },
          {
            id: "activate",
            label: "Ativar cliente",
            onClick: handleMenuClick,
            hide: (data) => role === 'master' && !data.active,
          },
          {
            id: "deactivate",
            label: "Desativar cliente",
            onClick: handleMenuClick,
            hide: (data) => role === 'master' && data.active,
          },
        ]}
      />

      {registerModal && (
        <StepRegisterClient
          opened={registerModal}
          setOpened={() => {
            setClient(undefined);
            setRegisterModal(false);

            if (hasChanges) {
              setHasChanges(false);
              window.location.reload();
            }
          }}
          clientData={client}
        />
      )}

      {client && (
        <MoreClientDetailsModal
          modalIsOpen={customerClient}
          setIsOpen={setCustomerClient}
          client={client}
        />
      )}

      <AlertModal
        isOpen={deleteModal}
        onRequestClose={() => setDeleteModal(false)}
        title={`Deseja desativar o cliente ${client?.name}?`}
        text="Um cliente desativado não poderá mais requisitar ocorrências à 
          central. Contudo, basta ativá-lo novamente para que o registro ocorra."
        confirmButtonText="Sim, desativar"
        confirmButtonClick={() => {
          handleClientDeleteSubmit({ client });
        }}
      />
      <MoreDetailsModal
        modalIsOpen={productDetails}
        setIsOpen={setProductDetails}
        product={client?.product}
      />
      <AlertModal
        isOpen={activationModal}
        onRequestClose={() => setActivationModal(false)}
        title={`Deseja reativar o cliente ${client?.name}?`}
        text="Isso irá sinalizar a todos que este é um cliente válido, 
          permitindo a criação de novas ocorrências para ele. Deseja continuar?"
        confirmButtonText="Sim, reativar cliente"
        confirmButtonClick={() => {
          handleClientActiveSubmit({ client });
        }}
      />
    </DashboardTemplate>
  );
}
